// Push notifications on the phone. After sign-in the app asks once for
// permission, registers with Firebase and gives the server its token; it does
// this at every start, because Firebase can change a token at any time and
// the server forgets ones that stop working. In a browser none of this runs.
import { PushNotifications } from '@capacitor/push-notifications';
import { isNative } from '../config.js';
import { api } from '../api/client.js';
import { openLink } from './links.js';

export const pushAvailable = isNative && import.meta.env.VITE_PUSH_AVAILABLE === true;

const TOKEN_KEY = 'nu.pushToken';
let state = pushAvailable ? 'unknown' : 'unavailable'; // unknown | on | blocked | error | unavailable
const watchers = new Set();
const foreground = new Set();

function setState(s) {
  state = s;
  watchers.forEach((fn) => fn(s));
}
export function pushState() { return state; }
export function onPushState(fn) { watchers.add(fn); return () => watchers.delete(fn); }
// Notifications that arrive while the app is open are not shown by Android;
// the shell shows them as a banner instead.
export function onForegroundNotification(fn) { foreground.add(fn); return () => foreground.delete(fn); }

function savedToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

// Taps can arrive before sign-in (cold start), so this is set up at boot.
export function startPushListeners() {
  if (!pushAvailable) return;
  PushNotifications.addListener('pushNotificationActionPerformed', (e) => {
    openLink(e.notification?.data?.link);
  });
  PushNotifications.addListener('pushNotificationReceived', (n) => {
    foreground.forEach((fn) => fn({ title: n.title, body: n.body, link: n.data?.link }));
  });
  PushNotifications.addListener('registration', async ({ value }) => {
    try {
      await api('/push/devices', { method: 'POST', body: { token: value, platform: 'android', appVersion: import.meta.env.VITE_APP_VERSION } });
      try { localStorage.setItem(TOKEN_KEY, value); } catch { /* re-sent next start anyway */ }
      setState('on');
    } catch {
      // Offline or server down: the phone is fine, the server learns the
      // token next time the app starts.
      setState('on');
    }
  });
  PushNotifications.addListener('registrationError', () => setState('error'));
}

// Call when someone is signed in.
export async function enablePush() {
  if (!pushAvailable) return;
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') { setState('blocked'); return; }
  await PushNotifications.createChannel({
    id: 'general',
    name: 'Notifications',
    description: 'Jobs, messages and updates from the office',
    importance: 4, // high: sound + heads-up
    visibility: 1,
  }).catch(() => {});
  await PushNotifications.register();
}

// Before signing out, so the next person on this phone doesn't get the last
// person's notifications.
export async function disablePush() {
  const token = savedToken();
  if (!pushAvailable || !token) return;
  await api('/push/devices', { method: 'DELETE', body: { token } }).catch(() => {});
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* nothing to do */ }
}
